import { useState, FC } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Alert,
    CircularProgress,
    Box,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useGetRecentSchedulesQuery, useCopyScheduleMutation } from '@/store/apiSlice';
import type { RecentSchedule } from '@/types';

interface CopyScheduleDialogProps {
    open: boolean;
    onClose: () => void;
    targetDate: string;
}

const CopyScheduleDialog: FC<CopyScheduleDialogProps> = ({ open, onClose, targetDate }) => {
    const { LL } = useI18nContext();
    const [selectedSchedule, setSelectedSchedule] = useState<string | null>(null);
    const { data: recentSchedules, isLoading } = useGetRecentSchedulesQuery(undefined, {
        skip: !open,
    });
    const [copySchedule, { isLoading: isCopying, error }] = useCopyScheduleMutation();

    const handleCopy = async () => {
        if (!selectedSchedule) return;

        try {
            await copySchedule({ fromDate: selectedSchedule, toDate: targetDate }).unwrap();
            onClose();
            setSelectedSchedule(null);
        } catch (err) {
            // Error is handled by the error state from the mutation
        }
    };

    const handleClose = () => {
        setSelectedSchedule(null);
        onClose();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{LL.scheduleModal.copyScheduleTitle()}</DialogTitle>
            <DialogContent>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: 2,
                    }}
                >
                    {LL.scheduleModal.copyScheduleDescription()}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {'status' in error && error.status === 409
                            ? LL.scheduleModal.alreadyExists()
                            : LL.scheduleModal.copyError()}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : recentSchedules && recentSchedules.length > 0 ? (
                    <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {recentSchedules.map((schedule: RecentSchedule) => (
                            <ListItem key={schedule.eventDate} disablePadding>
                                <ListItemButton
                                    selected={selectedSchedule === schedule.eventDate}
                                    onClick={() => setSelectedSchedule(schedule.eventDate)}
                                >
                                    <ListItemText
                                        primary={
                                            schedule.eventName || formatDate(schedule.eventDate)
                                        }
                                        secondary={`${formatDate(schedule.eventDate)} - ${LL.scheduleModal.itemsCount({ count: schedule.itemCount })}`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Alert severity="info">{LL.scheduleModal.noRecentSchedules()}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isCopying}>
                    {LL.common.cancel()}
                </Button>
                <Button
                    onClick={handleCopy}
                    variant="contained"
                    disabled={!selectedSchedule || isCopying}
                    startIcon={isCopying ? <CircularProgress size={20} /> : <CopyIcon />}
                >
                    {LL.scheduleModal.copyScheduleButton()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyScheduleDialog;
